/**
 * Site Search Module
 *
 * Replaces Ajax Search Pro. Fetches a compact content index from the REST
 * API once (on first focus), fuzzy-searches it client-side with Fuse.js and
 * renders results with matched-character highlighting. The desktop instance
 * also reproduces the header expand/collapse animation: collapsed icon
 * expands to a centered input, hiding the main navigation while open.
 */
(function () {
  "use strict";

  const INDEX_URL = "/wp-json/phw/v1/search-index";
  const MAX_RESULTS = 8;
  const DEBOUNCE_MS = 120;

  let indexPromise = null;

  function fetchIndex() {
    if (!indexPromise) {
      indexPromise = fetch(INDEX_URL)
        .then((r) => {
          if (!r.ok) throw new Error("search index request failed");
          return r.json();
        })
        .catch((err) => {
          indexPromise = null; // allow retry on next focus
          throw err;
        });
    }
    return indexPromise;
  }

  function buildFuse(items) {
    return new Fuse(items, {
      keys: [
        { name: "title", weight: 0.7 },
        { name: "content", weight: 0.3 },
      ],
      includeMatches: true,
      ignoreLocation: true,
      threshold: 0.35,
      minMatchCharLength: 2,
    });
  }

  // Highlight the query terms where they literally occur in the title
  // (like ASP's keyword highlighting). For pure fuzzy hits (typos) where
  // no term occurs verbatim, fall back to the single longest matched
  // region from Fuse — scattered per-letter marks read as noise.
  function highlight(text, query, regions) {
    const tokens = (query || "")
      .split(/\s+/)
      .map((t) => t.trim())
      .filter((t) => t.length >= 2)
      .map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));

    if (tokens.length) {
      const re = new RegExp("(" + tokens.join("|") + ")", "gi");
      if (re.test(text)) {
        return text
          .split(re)
          .map((part, i) =>
            i % 2 === 1
              ? "<mark>" + escapeHtml(part) + "</mark>"
              : escapeHtml(part)
          )
          .join("");
      }
    }

    if (regions && regions.length) {
      const longest = regions.reduce((a, b) =>
        b[1] - b[0] > a[1] - a[0] ? b : a
      );
      if (longest[1] - longest[0] >= 2) {
        return (
          escapeHtml(text.slice(0, longest[0])) +
          "<mark>" +
          escapeHtml(text.slice(longest[0], longest[1] + 1)) +
          "</mark>" +
          escapeHtml(text.slice(longest[1] + 1))
        );
      }
    }

    return escapeHtml(text);
  }

  function escapeHtml(str) {
    return str.replace(
      /[&<>"']/g,
      (c) =>
        ({
          "&": "&amp;",
          "<": "&lt;",
          ">": "&gt;",
          '"': "&quot;",
          "'": "&#39;",
        }[c])
    );
  }

  class SiteSearch {
    constructor(root) {
      this.root = root;
      this.form = root.querySelector(".phw-search-form");
      this.input = root.querySelector(".phw-search-input");
      this.clearBtn = root.querySelector(".phw-search-clear");
      this.toggleBtn = root.querySelector(".phw-search-toggle");
      this.resultsEl = root.querySelector(".phw-search-results");
      this.isDesktopHeader = root.dataset.variant === "desktop";
      this.postTypes = (root.dataset.postTypes || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      this.fuse = null;
      this.activeIndex = -1;
      this.results = [];
      this.debounceTimer = null;

      this.bind();
    }

    bind() {
      this.input.addEventListener("focus", () => {
        this.expand();
        this.warmIndex();
      });

      if (this.toggleBtn) {
        this.toggleBtn.addEventListener("click", () => {
          this.expand();
          this.input.focus();
        });
      }

      this.input.addEventListener("input", () => {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => this.search(), DEBOUNCE_MS);
      });

      this.input.addEventListener("keydown", (e) => this.onKeydown(e));

      if (this.clearBtn) {
        this.clearBtn.addEventListener("click", () => {
          this.input.value = "";
          this.renderResults([]);
          this.input.focus();
        });
      }

      // Close on click outside; collapse desktop header variant
      document.addEventListener("pointerdown", (e) => {
        if (!this.root.contains(e.target)) {
          this.renderResults([]);
          this.collapse();
        }
      });

      this.input.addEventListener("blur", () => {
        // Delay so result clicks land before the dropdown goes away
        setTimeout(() => {
          if (!this.root.contains(document.activeElement)) {
            this.renderResults([]);
            this.collapse();
          }
        }, 150);
      });
    }

    warmIndex() {
      fetchIndex()
        .then((items) => {
          if (!this.fuse) {
            const filtered = this.postTypes.length
              ? items.filter((i) => this.postTypes.includes(i.type))
              : items;
            this.fuse = buildFuse(filtered);
            // If the user typed before the index arrived, search now
            if (this.input.value.trim().length > 1) {
              this.search();
            }
          }
        })
        .catch(() => {
          /* graceful: form still submits to the WP search page */
        });
    }

    expand() {
      if (!this.isDesktopHeader || this.root.classList.contains("expanded")) {
        return;
      }
      this.root.classList.add("expanded");
      const container = this.root.closest("#navSearchContainer");
      if (container) container.classList.add("expanded");
      const header = this.root.closest(".header");
      if (header) header.classList.add("search-open");
    }

    collapse() {
      if (!this.isDesktopHeader) return;
      this.root.classList.remove("expanded");
      const container = this.root.closest("#navSearchContainer");
      if (container) container.classList.remove("expanded");
      const header = this.root.closest(".header");
      if (header) header.classList.remove("search-open");
      this.input.value = "";
    }

    search() {
      const q = this.input.value.trim();
      if (q.length < 2 || !this.fuse) {
        this.renderResults([]);
        return;
      }
      this.results = this.fuse.search(q).slice(0, MAX_RESULTS);
      this.renderResults(this.results);
    }

    renderResults(results) {
      this.activeIndex = -1;

      if (!results.length) {
        const q = this.input.value.trim();
        if (q.length >= 2 && this.fuse && results === this.results) {
          this.resultsEl.innerHTML =
            '<div class="phw-search-empty">No results found</div>';
          this.resultsEl.hidden = false;
        } else {
          this.resultsEl.innerHTML = "";
          this.resultsEl.hidden = true;
        }
        return;
      }

      const html = results
        .map((r, i) => {
          const titleMatch = (r.matches || []).find(
            (m) => m.key === "title"
          );
          const title = highlight(
            r.item.title,
            this.input.value.trim(),
            titleMatch ? titleMatch.indices : null
          );
          return (
            '<a class="phw-search-result" role="option" id="phw-search-result-' +
            i +
            '" href="' +
            escapeHtml(r.item.url) +
            '"><span class="phw-search-result-label">' +
            escapeHtml(r.item.label) +
            '</span><span class="phw-search-result-title">' +
            title +
            "</span></a>"
          );
        })
        .join("");

      this.resultsEl.innerHTML = html;
      this.resultsEl.hidden = false;
    }

    onKeydown(e) {
      const items = this.resultsEl.querySelectorAll(".phw-search-result");

      if (e.key === "Escape") {
        this.renderResults([]);
        this.input.blur();
        this.collapse();
        return;
      }

      if (e.key === "Enter") {
        if (this.activeIndex >= 0 && items[this.activeIndex]) {
          e.preventDefault();
          window.location.href = items[this.activeIndex].href;
        }
        // Otherwise let the form submit to the WP search page
        return;
      }

      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        if (!items.length) return;
        e.preventDefault();
        const delta = e.key === "ArrowDown" ? 1 : -1;
        this.activeIndex =
          (this.activeIndex + delta + items.length) % items.length;
        items.forEach((el, i) => {
          el.classList.toggle("is-active", i === this.activeIndex);
        });
        this.input.setAttribute(
          "aria-activedescendant",
          items[this.activeIndex].id
        );
      }
    }
  }

  document.addEventListener("DOMContentLoaded", () => {
    if (typeof Fuse === "undefined") {
      console.warn("Fuse.js not loaded; site search falls back to WP search");
      return;
    }
    document
      .querySelectorAll("[data-phw-search]")
      .forEach((el) => new SiteSearch(el));
  });
})();
